import type { BaseItem, NullablePartial } from '../common.js';

export enum PointReference {
  LeftTop = 'LEFT_TOP',
  LeftBottom = 'LEFT_BOTTOM',
  RightTop = 'RIGHT_TOP',
  RightBottom = 'RIGHT_BOTTOM',
}

export enum DataSource {
  Crossmark = 'CROSSMARK',
  DateSigned = 'DATE_SIGNED',
  EhrScheduled = 'EHR_SCHEDULED',
  EmergencyContactName = 'EMERGENCY_CONTACT_NAME',
  EmergencyContactRelationship = 'EMERGENCY_CONTACT_RELATIONSHIP',
  EmergencyContactPhone = 'EMERGENCY_CONTACT_PHONE',
  GuardianRelationship = 'GUARDIAN_RELATIONSHIP',
  MRN = 'MRN', // deprecated use PATIENT_MRN
  None = 'NONE',
  Signature = 'SIGNATURE',

  GuarantorAge = 'GUARANTOR_AGE',
  GuarantorDOB = 'GUARANTOR_DOB',
  GuarantorHomeAddress = 'GUARANTOR_HOME_ADDRESS',
  GuarantorHomeFullAddress = 'GUARANTOR_HOME_FULL_ADDRESS',
  GuarantorHomeCityStateZip = 'GUARANTOR_HOME_CITY_STATE_ZIP',
  GuarantorHomePhoneNumber = 'GUARANTOR_HOME_PHONE_NUMBER',
  GuarantorName = 'GUARANTOR_NAME',
  GuarantorRelationship = 'GUARANTOR_RELATIONSHIP', // deprecated use GUARANTOR_RELATIONSHIP_TO_PATIENT
  GuarantorRelationshipToPatient = 'GUARANTOR_RELATIONSHIP_TO_PATIENT',
  GuarantorSex = 'GUARANTOR_SEX',
  GuarantorSSN = 'GUARANTOR_SSN',

  PatientAge = 'PATIENT_AGE',
  NewbornAge = 'NEWBORN_AGE',
  PatientBirthGestationalAge = 'PATIENT_BIRTH_GESTATIONAL_AGE',
  PatientBirthWeightGrams = 'PATIENT_BIRTH_WEIGHT_GRAMS',
  PatientDOB = 'PATIENT_DOB',
  PatientEmail = 'PATIENT_EMAIL',
  PatientEmployer = 'PATIENT_EMPLOYER',
  PatientPreferredLanguage = 'PATIENT_PREFERRED_LANGUAGE',
  PatientCareProviderAddress = 'PATIENT_CARE_PROVIDER_ADDRESS',
  PatientCareProviderName = 'PATIENT_CARE_PROVIDER_NAME',
  PatientHomeAddress = 'PATIENT_HOME_ADDRESS',
  PatientHomeFullAddress = 'PATIENT_HOME_FULL_ADDRESS',
  PatientHomeCityStateZip = 'PATIENT_HOME_CITY_STATE_ZIP',
  PatientHomePhoneNumber = 'PATIENT_HOME_PHONE_NUMBER',
  PatientInitials = 'PATIENT_INITIALS',
  PatientIP = 'PATIENT_IP',
  PatientMobilePhoneNumber = 'PATIENT_MOBILE_PHONE_NUMBER',
  PatientMotherName = 'PATIENT_MOTHER_NAME',
  PatientMRN = 'PATIENT_MRN',
  PatientPharmacyNameWithAddress = 'PATIENT_PHARMACY_NAME_WITH_ADDRESS',
  PatientPharmacyPhone = 'PATIENT_PHARMACY_PHONE',
  PatientAthenaIdxMrn = 'PATIENT_ATHENA_IDX_MRN',
  PatientName = 'PATIENT_NAME',
  PatientRace = 'PATIENT_RACE',
  PatientSex = 'PATIENT_SEX',
  PatientSSN = 'PATIENT_SSN',

  PrimaryInsuranceGroupNumber = 'PRIMARY_INSURANCE_GROUP_NUMBER',
  PrimaryInsuranceInsuranceName = 'PRIMARY_INSURANCE_INSURANCE_NAME',
  PrimaryInsurancePolicyHolderAge = 'PRIMARY_INSURANCE_POLICY_HOLDER_AGE',
  PrimaryInsurancePolicyHolderDOB = 'PRIMARY_INSURANCE_POLICY_HOLDER_DOB',
  PrimaryInsurancePolicyHolderHomeAddress = 'PRIMARY_INSURANCE_POLICY_HOLDER_HOME_ADDRESS',
  PrimaryInsurancePolicyHolderHomeCityStateZip = 'PRIMARY_INSURANCE_POLICY_HOLDER_HOME_CITY_STATE_ZIP',
  PrimaryInsurancePolicyHolderHomePhoneNumber = 'PRIMARY_INSURANCE_POLICY_HOLDER_HOME_PHONE_NUMBER',
  PrimaryInsurancePolicyHolderName = 'PRIMARY_INSURANCE_POLICY_HOLDER_NAME',
  PrimaryInsurancePolicyHolderRelationshipToPatient = 'PRIMARY_INSURANCE_POLICY_HOLDER_RELATIONSHIP_TO_PATIENT',
  PrimaryInsurancePolicyHolderSex = 'PRIMARY_INSURANCE_POLICY_HOLDER_SEX',
  PrimaryInsurancePolicyNumber = 'PRIMARY_INSURANCE_POLICY_NUMBER',

  SecondaryInsuranceGroupNumber = 'SECONDARY_INSURANCE_GROUP_NUMBER',
  SecondaryInsuranceInsuranceName = 'SECONDARY_INSURANCE_INSURANCE_NAME',
  SecondaryInsurancePolicyHolderAge = 'SECONDARY_INSURANCE_POLICY_HOLDER_AGE',
  SecondaryInsurancePolicyHolderDOB = 'SECONDARY_INSURANCE_POLICY_HOLDER_DOB',
  SecondaryInsurancePolicyHolderHomeAddress = 'SECONDARY_INSURANCE_POLICY_HOLDER_HOME_ADDRESS',
  SecondaryInsurancePolicyHolderHomeCityStateZip = 'SECONDARY_INSURANCE_POLICY_HOLDER_HOME_CITY_STATE_ZIP',
  SecondaryInsurancePolicyHolderHomePhoneNumber = 'SECONDARY_INSURANCE_POLICY_HOLDER_HOME_PHONE_NUMBER',
  SecondaryInsurancePolicyHolderName = 'SECONDARY_INSURANCE_POLICY_HOLDER_NAME',
  SecondaryInsurancePolicyHolderRelationshipToPatient = 'SECONDARY_INSURANCE_POLICY_HOLDER_RELATIONSHIP_TO_PATIENT',
  SecondaryInsurancePolicyHolderSex = 'SECONDARY_INSURANCE_POLICY_HOLDER_SEX',
  SecondaryInsurancePolicyNumber = 'SECONDARY_INSURANCE_POLICY_NUMBER',

  VisitAdmitDate = 'VISIT_ADMIT_DATE',
  VisitAdmitDatetime = 'VISIT_ADMIT_DATETIME',
  VisitAdmittingPhysicianName = 'VISIT_ADMITTING_PHYSICIAN_NAME',
  VisitAttendingPhysicianName = 'VISIT_ATTENDING_PHYSICIAN_NAME',
  VisitBuildingLocation = 'VISIT_BUILDING_LOCATION',
  VisitEHRID = 'VISIT_EHR_ID',
  VisitEHRType = 'VISIT_EHR_TYPE',
  VisitMedicalService = 'VISIT_MEDICAL_SERVICE',
  VisitReason = 'VISIT_REASON',
}

export enum DataSourceApiKey {
  Crossmark = 'crossmark',
  DateSigned = 'dateSigned',
  EhrScheduled = 'ehrScheduled',
  EmergencyContactName = 'emergencyContactName',
  EmergencyContactPhone = 'emergencyContactPhone',
  EmergencyContactRelationship = 'emergencyContactRelationship',
  GuarantorAge = 'guarantorAge',
  GuarantorDob = 'guarantorDob',
  GuarantorHomeAddress = 'guarantorHomeAddress',
  GuarantorHomeCityStateZip = 'guarantorHomeCityStateZip',
  GuarantorHomeFullAddress = 'guarantorHomeFullAddress',
  GuarantorHomePhoneNumber = 'guarantorHomePhoneNumber',
  GuarantorName = 'guarantorName',
  GuarantorRelationship = 'guarantorRelationship', // deprecated use guarantorRelationshipToPatient
  GuarantorRelationshipToPatient = 'guarantorRelationshipToPatient',
  GuarantorSex = 'guarantorSex',
  GuarantorSsn = 'guarantorSsn',
  GuardianRelationship = 'guardianRelationship',
  Mrn = 'mrn', // deprecated use patientMrn
  NewbornAge = 'newbornAge',
  None = 'none',
  PatientAge = 'patientAge',
  PatientAthenaIdxMrn = 'patientAthenaIdxMrn',
  PatientBirthGestationalAge = 'patientBirthGestationalAge',
  PatientBirthWeightGrams = 'patientBirthWeightGrams',
  PatientCareProviderAddress = 'patientCareProviderAddress',
  PatientCareProviderName = 'patientCareProviderName',
  PatientDob = 'patientDob',
  PatientEmail = 'patientEmail',
  PatientEmployer = 'patientEmployer',
  PatientHomeAddress = 'patientHomeAddress',
  PatientHomeCityStateZip = 'patientHomeCityStateZip',
  PatientHomeFullAddress = 'patientHomeFullAddress',
  PatientHomePhoneNumber = 'patientHomePhoneNumber',
  PatientInitials = 'patientInitials',
  PatientIp = 'patientIp',
  PatientMobilePhoneNumber = 'patientMobilePhoneNumber',
  PatientMotherName = 'patientMotherName',
  PatientMrn = 'patientMrn',
  PatientName = 'patientName',
  PatientPharmacyNameWithAddress = 'patientPharmacyNameWithAddress',
  PatientPharmacyPhone = 'patientPharmacyPhone',
  PatientPreferredLanguage = 'patientPreferredLanguage',
  PatientRace = 'patientRace',
  PatientSex = 'patientSex',
  PatientSsn = 'patientSsn',
  PrimaryInsuranceGroupNumber = 'primaryInsuranceGroupNumber',
  PrimaryInsuranceInsuranceName = 'primaryInsuranceInsuranceName',
  PrimaryInsurancePolicyHolderAge = 'primaryInsurancePolicyHolderAge',
  PrimaryInsurancePolicyHolderDOB = 'primaryInsurancePolicyHolderDob',
  PrimaryInsurancePolicyHolderHomeAddress = 'primaryInsurancePolicyHolderHomeAddress',
  PrimaryInsurancePolicyHolderHomeCityStateZip = 'primaryInsurancePolicyHolderHomeCityStateZip',
  PrimaryInsurancePolicyHolderHomePhoneNumber = 'primaryInsurancePolicyHolderHomePhoneNumber',
  PrimaryInsurancePolicyHolderName = 'primaryInsurancePolicyHolderName',
  PrimaryInsurancePolicyHolderRelationshipToPatient = 'primaryInsurancePolicyHolderRelationshipToPatient',
  PrimaryInsurancePolicyHolderSex = 'primaryInsurancePolicyHolderSex',
  PrimaryInsurancePolicyNumber = 'primaryInsurancePolicyNumber',
  SecondaryInsuranceGroupNumber = 'secondaryInsuranceGroupNumber',
  SecondaryInsuranceInsuranceName = 'secondaryInsuranceInsuranceName',
  SecondaryInsurancePolicyHolderAge = 'secondaryInsurancePolicyHolderAge',
  SecondaryInsurancePolicyHolderDOB = 'secondaryInsurancePolicyHolderDob',
  SecondaryInsurancePolicyHolderHomeAddress = 'secondaryInsurancePolicyHolderHomeAddress',
  SecondaryInsurancePolicyHolderHomeCityStateZip = 'secondaryInsurancePolicyHolderHomeCityStateZip',
  SecondaryInsurancePolicyHolderHomePhoneNumber = 'secondaryInsurancePolicyHolderHomePhoneNumber',
  SecondaryInsurancePolicyHolderName = 'secondaryInsurancePolicyHolderName',
  SecondaryInsurancePolicyHolderRelationshipToPatient = 'secondaryInsurancePolicyHolderRelationshipToPatient',
  SecondaryInsurancePolicyHolderSex = 'secondaryInsurancePolicyHolderSex',
  SecondaryInsurancePolicyNumber = 'secondaryInsurancePolicyNumber',
  Signature = 'signature',
  VisitAdmitDate = 'visitAdmitDate',
  VisitAdmitDatetime = 'visitAdmitDatetime',
  VisitAdmittingPhysicianName = 'visitAdmittingPhysicianName',
  VisitAttendingPhysicianName = 'visitAttendingPhysicianName',
  VisitBuildingLocation = 'visitBuildingLocation',
  VisitEhrId = 'visitEhrId',
  VisitEhrType = 'visitEhrType',
  VisitMedicalService = 'visitMedicalService',
  VisitReason = 'visitReason',
}

export const DataSourceToDataSourceApiKeys: Record<DataSource, DataSourceApiKey> = {
  [DataSource.Crossmark]: DataSourceApiKey.Crossmark,
  [DataSource.DateSigned]: DataSourceApiKey.DateSigned,
  [DataSource.EhrScheduled]: DataSourceApiKey.EhrScheduled,
  [DataSource.EmergencyContactName]: DataSourceApiKey.EmergencyContactName,
  [DataSource.EmergencyContactPhone]: DataSourceApiKey.EmergencyContactPhone,
  [DataSource.EmergencyContactRelationship]: DataSourceApiKey.EmergencyContactRelationship,
  [DataSource.GuarantorAge]: DataSourceApiKey.GuarantorAge,
  [DataSource.GuarantorDOB]: DataSourceApiKey.GuarantorDob,
  [DataSource.GuarantorHomeAddress]: DataSourceApiKey.GuarantorHomeAddress,
  [DataSource.GuarantorHomeCityStateZip]: DataSourceApiKey.GuarantorHomeCityStateZip,
  [DataSource.GuarantorHomeFullAddress]: DataSourceApiKey.GuarantorHomeFullAddress,
  [DataSource.GuarantorHomePhoneNumber]: DataSourceApiKey.GuarantorHomePhoneNumber,
  [DataSource.GuarantorName]: DataSourceApiKey.GuarantorName,
  [DataSource.GuarantorRelationship]: DataSourceApiKey.GuarantorRelationship,
  [DataSource.GuarantorRelationshipToPatient]: DataSourceApiKey.GuarantorRelationshipToPatient,
  [DataSource.GuarantorSex]: DataSourceApiKey.GuarantorSex,
  [DataSource.GuarantorSSN]: DataSourceApiKey.GuarantorSsn,
  [DataSource.GuardianRelationship]: DataSourceApiKey.GuardianRelationship,
  [DataSource.MRN]: DataSourceApiKey.Mrn,
  [DataSource.NewbornAge]: DataSourceApiKey.NewbornAge,
  [DataSource.None]: DataSourceApiKey.None,
  [DataSource.PatientAge]: DataSourceApiKey.PatientAge,
  [DataSource.PatientAthenaIdxMrn]: DataSourceApiKey.PatientAthenaIdxMrn,
  [DataSource.PatientBirthGestationalAge]: DataSourceApiKey.PatientBirthGestationalAge,
  [DataSource.PatientBirthWeightGrams]: DataSourceApiKey.PatientBirthWeightGrams,
  [DataSource.PatientCareProviderAddress]: DataSourceApiKey.PatientCareProviderAddress,
  [DataSource.PatientCareProviderName]: DataSourceApiKey.PatientCareProviderName,
  [DataSource.PatientDOB]: DataSourceApiKey.PatientDob,
  [DataSource.PatientEmail]: DataSourceApiKey.PatientEmail,
  [DataSource.PatientEmployer]: DataSourceApiKey.PatientEmployer,
  [DataSource.PatientHomeAddress]: DataSourceApiKey.PatientHomeAddress,
  [DataSource.PatientHomeCityStateZip]: DataSourceApiKey.PatientHomeCityStateZip,
  [DataSource.PatientHomeFullAddress]: DataSourceApiKey.PatientHomeFullAddress,
  [DataSource.PatientHomePhoneNumber]: DataSourceApiKey.PatientHomePhoneNumber,
  [DataSource.PatientInitials]: DataSourceApiKey.PatientInitials,
  [DataSource.PatientIP]: DataSourceApiKey.PatientIp,
  [DataSource.PatientMobilePhoneNumber]: DataSourceApiKey.PatientMobilePhoneNumber,
  [DataSource.PatientMotherName]: DataSourceApiKey.PatientMotherName,
  [DataSource.PatientMRN]: DataSourceApiKey.PatientMrn,
  [DataSource.PatientName]: DataSourceApiKey.PatientName,
  [DataSource.PatientPharmacyNameWithAddress]: DataSourceApiKey.PatientPharmacyNameWithAddress,
  [DataSource.PatientPharmacyPhone]: DataSourceApiKey.PatientPharmacyPhone,
  [DataSource.PatientPreferredLanguage]: DataSourceApiKey.PatientPreferredLanguage,
  [DataSource.PatientRace]: DataSourceApiKey.PatientRace,
  [DataSource.PatientSex]: DataSourceApiKey.PatientSex,
  [DataSource.PatientSSN]: DataSourceApiKey.PatientSsn,
  [DataSource.PrimaryInsuranceGroupNumber]: DataSourceApiKey.PrimaryInsuranceGroupNumber,
  [DataSource.PrimaryInsuranceInsuranceName]: DataSourceApiKey.PrimaryInsuranceInsuranceName,
  [DataSource.PrimaryInsurancePolicyHolderAge]: DataSourceApiKey.PrimaryInsurancePolicyHolderAge,
  [DataSource.PrimaryInsurancePolicyHolderDOB]: DataSourceApiKey.PrimaryInsurancePolicyHolderDOB,
  [DataSource.PrimaryInsurancePolicyHolderHomeAddress]:
    DataSourceApiKey.PrimaryInsurancePolicyHolderHomeAddress,
  [DataSource.PrimaryInsurancePolicyHolderHomeCityStateZip]:
    DataSourceApiKey.PrimaryInsurancePolicyHolderHomeCityStateZip,
  [DataSource.PrimaryInsurancePolicyHolderHomePhoneNumber]:
    DataSourceApiKey.PrimaryInsurancePolicyHolderHomePhoneNumber,
  [DataSource.PrimaryInsurancePolicyHolderName]: DataSourceApiKey.PrimaryInsurancePolicyHolderName,
  [DataSource.PrimaryInsurancePolicyHolderRelationshipToPatient]:
    DataSourceApiKey.PrimaryInsurancePolicyHolderRelationshipToPatient,
  [DataSource.PrimaryInsurancePolicyHolderSex]: DataSourceApiKey.PrimaryInsurancePolicyHolderSex,
  [DataSource.PrimaryInsurancePolicyNumber]: DataSourceApiKey.PrimaryInsurancePolicyNumber,
  [DataSource.SecondaryInsuranceGroupNumber]: DataSourceApiKey.SecondaryInsuranceGroupNumber,
  [DataSource.SecondaryInsuranceInsuranceName]: DataSourceApiKey.SecondaryInsuranceInsuranceName,
  [DataSource.SecondaryInsurancePolicyHolderAge]:
    DataSourceApiKey.SecondaryInsurancePolicyHolderAge,
  [DataSource.SecondaryInsurancePolicyHolderDOB]:
    DataSourceApiKey.SecondaryInsurancePolicyHolderDOB,
  [DataSource.SecondaryInsurancePolicyHolderHomeAddress]:
    DataSourceApiKey.SecondaryInsurancePolicyHolderHomeAddress,
  [DataSource.SecondaryInsurancePolicyHolderHomeCityStateZip]:
    DataSourceApiKey.SecondaryInsurancePolicyHolderHomeCityStateZip,
  [DataSource.SecondaryInsurancePolicyHolderHomePhoneNumber]:
    DataSourceApiKey.SecondaryInsurancePolicyHolderHomePhoneNumber,
  [DataSource.SecondaryInsurancePolicyHolderName]:
    DataSourceApiKey.SecondaryInsurancePolicyHolderName,
  [DataSource.SecondaryInsurancePolicyHolderRelationshipToPatient]:
    DataSourceApiKey.SecondaryInsurancePolicyHolderRelationshipToPatient,
  [DataSource.SecondaryInsurancePolicyHolderSex]:
    DataSourceApiKey.SecondaryInsurancePolicyHolderSex,
  [DataSource.SecondaryInsurancePolicyNumber]: DataSourceApiKey.SecondaryInsurancePolicyNumber,
  [DataSource.Signature]: DataSourceApiKey.Signature,
  [DataSource.VisitAdmitDate]: DataSourceApiKey.VisitAdmitDate,
  [DataSource.VisitAdmitDatetime]: DataSourceApiKey.VisitAdmitDatetime,
  [DataSource.VisitAdmittingPhysicianName]: DataSourceApiKey.VisitAdmittingPhysicianName,
  [DataSource.VisitAttendingPhysicianName]: DataSourceApiKey.VisitAttendingPhysicianName,
  [DataSource.VisitBuildingLocation]: DataSourceApiKey.VisitBuildingLocation,
  [DataSource.VisitEHRID]: DataSourceApiKey.VisitEhrId,
  [DataSource.VisitEHRType]: DataSourceApiKey.VisitEhrType,
  [DataSource.VisitMedicalService]: DataSourceApiKey.VisitMedicalService,
  [DataSource.VisitReason]: DataSourceApiKey.VisitReason,
};

export enum Font {
  Helvetica = 'Helvetica',
  SignPainterHouseScript = 'SignPainterHouseScript',
  UbuntuR = 'Ubuntu R',
}

export enum SigningFor {
  Myself = 'myself',
  Guardian = 'guardian',
}

export interface Command {
  dataSource: string;
  font: Font;
  fontSize: number;
  fontColorR: number; // 0-255
  fontColorG: number; // 0-255
  fontColorB: number; // 0-255
  pageIndex: number;
  // 0, 0 is bottom left of the PDF page
  pointX: number;
  pointY: number;
  pointRef: PointReference;
  lineWidth?: number;
  mappedValue?: DataSource.Crossmark | DataSource.None;
  previsitFormFieldId?: string;
  value?: string;
}

export enum Category {
  ConditionsOfAdmission = 'CONDITIONS_OF_ADMISSION',
  EncounterDocumentPatientHistory = 'ENCOUNTER_DOCUMENT_PATIENT_HISTORY',
  FinancialPolicy = 'FINANCIAL_POLICY',
  FormSummary = 'FORM_SUMMARY',
  HealthInformationExchange = 'HEALTH_INFORMATION_EXCHANGE',
  HealthInformationExchangeAcknowledgment = 'HEALTH_INFORMATION_EXCHANGE_ACKNOWLEDGMENT',
  MedicationPolicy = 'MEDICATION_POLICY',
  NoticeOfAdmission = 'NOTICE_OF_ADMISSION',
  PatientAuthorization = 'PATIENT_AUTHORIZATION',
  PatientChargeNotice = 'PATIENT_CHARGE_NOTICE',
  PatientConsent = 'PATIENT_CONSENT',
  PatientConsentBehavioralHealth = 'PATIENT_CONSENT_BEHAVIORAL_HEALTH',
  PatientConsentEstablishedPatient = 'PATIENT_CONSENT_ESTABLISHED_PATIENT',
  PatientConsentNewPatient = 'PATIENT_CONSENT_NEW_PATIENT',
  PatientConsentRemoteMonitoring = 'PATIENT_CONSENT_REMOTE_MONITORING',
  PatientFeeDisclosure = 'PATIENT_FEE_DISCLOSURE',
  PatientRelease = 'PATIENT_RELEASE',
  PatientSpecialtyConsent = 'PATIENT_SPECIALTY_CONSENT',
  PatientTranscriptionConsent = 'PATIENT_TRANSCRIPTION_CONSENT',
  PrivacyPolicy = 'PRIVACY_POLICY',
  TreatmentConsent = 'TREATMENT_CONSENT',
  VirtualConsent = 'VIRTUAL_CONSENT',
}

export interface BaseDocumentTemplate {
  name: string;
  category: Category;
  commands: Command[];
  gsPath: string;
  fullReviewRequired: boolean;
  viewRequired: boolean;
  deleted: Date | null;
  organizationId: string | null;
  internalName: string | null;
}

export interface DocumentTemplate extends BaseItem, BaseDocumentTemplate {}

export type CreateValues = NullablePartial<
  Omit<BaseDocumentTemplate, 'fullReviewRequired' | 'viewRequired'>
> & {
  fullReviewRequired?: boolean;
  viewRequired?: boolean;
};
export type UpdateValues = Partial<BaseDocumentTemplate>;

export interface FindAllOptions extends Partial<
  Pick<DocumentTemplate, 'name' | 'category' | 'organizationId' | 'internalName'>
> {
  q?: string;
  includeDeleted?: boolean;
}
